import React, { useState } from "react";
import {
  Database,
  Globe,
  ServerCog,
  Shield,
  ToggleLeft,
  ToggleRight,
  Plus,
  X,
  Edit3,
  Eye,
  Search,
  Trash2,
} from "lucide-react";
import { useAdminSettings } from "../../hooks/useAdmin";
import { useRoutingRules } from "../../hooks/useRoutingRules";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { AdminLlmKeys } from "./AdminLlmKeys";
import { readApiError } from "../../utils/readApiError";

export function AdminSettings() {
  const {
    routingRules,
    capabilities,
    configuration,
    isLoading,
    error,
    updateRule,
    deleteRule,
    updateCapability,
    isUpdating,
    isDeletingRule,
  } = useAdminSettings();

  const {
    createRule,
    createCapability,
    isCreatingRule,
    isCreatingCapability,
  } = useRoutingRules();

  const [actionError, setActionError] = useState(null);
  // Disabling a route or retiring a rule takes a corridor offline, so the
  // backend requires a reason and records it. These used to be a bare toggle
  // and a window.confirm, which sent no reason at all - every disable and
  // every delete was rejected with a 422 the UI then reported as a generic
  // "could not update".
  const [pendingAction, setPendingAction] = useState(null);

  // Search state
  const [ruleSearchQuery, setRuleSearchQuery] = useState("");
  const [capabilitySearchQuery, setCapabilitySearchQuery] = useState("");

  // Modals & Drawers state
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [showCapabilityForm, setShowCapabilityForm] = useState(false);

  // Editing state
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingCapabilityId, setEditingCapabilityId] = useState(null);

  // Inspection modal state
  const [inspectItem, setInspectItem] = useState(null);
  const [inspectType, setInspectType] = useState(null); // 'rule' | 'capability'

  const [ruleForm, setRuleForm] = useState({
    corridor_code: "",
    sender_country: "",
    recipient_country: "",
    send_currency: "",
    receive_currency: "",
    funding_method: "",
    onramp_provider: "",
    onramp_rail: "",
    settlement_asset: "USDC",
    settlement_chain: "",
    offramp_provider: "",
    offramp_channel: "",
    offramp_network: "",
    min_amount: 0,
    max_amount: 0,
    priority: 100,
    enabled: true,
    valid_from: "",
    valid_to: "",
  });

  const [capabilityForm, setCapabilityForm] = useState({
    provider: "",
    country_code: "",
    currency: "",
    direction: "",
    channel: "",
    rail: "",
    network: "",
    enabled: true,
  });

  // Filtered routing rules based on search query
  const filteredRoutingRules = routingRules.filter((rule) => {
    const query = ruleSearchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      rule.corridor_code?.toLowerCase().includes(query) ||
      rule.send_currency?.toLowerCase().includes(query) ||
      rule.receive_currency?.toLowerCase().includes(query) ||
      rule.sender_country?.toLowerCase().includes(query) ||
      rule.recipient_country?.toLowerCase().includes(query) ||
      rule.onramp_provider?.toLowerCase().includes(query) ||
      rule.offramp_provider?.toLowerCase().includes(query) ||
      rule.funding_method?.toLowerCase().includes(query)
    );
  });

  // Filtered provider capabilities based on search query
  const filteredCapabilities = capabilities.filter((cap) => {
    const query = capabilitySearchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      cap.provider?.toLowerCase().includes(query) ||
      cap.country_code?.toLowerCase().includes(query) ||
      cap.currency?.toLowerCase().includes(query) ||
      cap.direction?.toLowerCase().includes(query) ||
      cap.channel?.toLowerCase().includes(query) ||
      cap.rail?.toLowerCase().includes(query) ||
      cap.network?.toLowerCase().includes(query)
    );
  });

  function describeRule(rule) {
    return [rule.source_country, rule.destination_country]
      .filter(Boolean)
      .join(" → ") || "this routing rule";
  }

  function describeCapability(capability) {
    return (
      [capability.provider, capability.country_code, capability.currency]
        .filter(Boolean)
        .join(" · ") || "this capability"
    );
  }

  async function toggleRule(rule) {
    setActionError(null);
    // Enabling restores service and needs no justification; disabling can take
    // a corridor offline, so it is confirmed and reasoned.
    if (rule.enabled) {
      setPendingAction({
        title: `Disable routing for ${describeRule(rule)}?`,
        description:
          "Transfers matching this rule will stop routing through it. If it is the last enabled rule for the corridor, the corridor stops working.",
        confirmLabel: "Disable rule",
        destructive: true,
        requireReason: true,
        run: (reason) =>
          updateRule({ ruleId: rule.id, payload: { enabled: false, reason } }),
        fallbackError: "Could not disable the routing rule.",
      });
      return;
    }
    try {
      await updateRule({ ruleId: rule.id, payload: { enabled: true } });
    } catch (err) {
      setActionError(readApiError(err, "Could not update the routing rule."));
    }
  }

  function handleDeleteRule(rule) {
    setActionError(null);
    if (rule.enabled) {
      setActionError("Disable the routing rule before deleting it.");
      return;
    }
    setPendingAction({
      title: `Permanently retire the ${describeRule(rule)} rule?`,
      description:
        "Rules referenced by a past routing decision cannot be retired - they must remain for audit history.",
      confirmLabel: "Retire rule",
      destructive: true,
      requireReason: true,
      // The mutation takes an object; passing a bare id left `ruleId`
      // undefined and produced a request to /routing-rules/undefined.
      run: (reason) => deleteRule({ ruleId: rule.id, reason }),
      fallbackError:
        "This rule cannot be deleted. Rules used by a routing decision must remain for audit history.",
    });
  }

  async function toggleCapability(capability) {
    setActionError(null);
    if (capability.enabled) {
      setPendingAction({
        title: `Disable ${describeCapability(capability)}?`,
        description:
          "Routing will stop selecting this provider capability. In-flight transfers are unaffected.",
        confirmLabel: "Disable capability",
        destructive: true,
        requireReason: true,
        run: (reason) =>
          updateCapability({
            capabilityId: capability.id,
            payload: { enabled: false, reason },
          }),
        fallbackError: "Could not disable the provider capability.",
      });
      return;
    }
    try {
      await updateCapability({
        capabilityId: capability.id,
        payload: { enabled: true },
      });
    } catch (err) {
      setActionError(readApiError(err, "Could not update the provider capability."));
    }
  }

  async function runPendingAction(reason) {
    if (!pendingAction) return;
    setActionError(null);
    try {
      await pendingAction.run(reason);
      setPendingAction(null);
    } catch (err) {
      setActionError(readApiError(err, pendingAction.fallbackError));
    }
  }

  function updateRuleField(field, value) {
    setRuleForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateCapabilityField(field, value) {
    setCapabilityForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function resetRuleForm() {
    setRuleForm({
      corridor_code: "",
      sender_country: "",
      recipient_country: "",
      send_currency: "",
      receive_currency: "",
      funding_method: "",
      onramp_provider: "",
      onramp_rail: "",
      settlement_asset: "USDC",
      settlement_chain: "",
      offramp_provider: "",
      offramp_channel: "",
      offramp_network: "",
      min_amount: 0,
      max_amount: 0,
      priority: 100,
      enabled: true,
      valid_from: "",
      valid_to: "",
    });
    setEditingRuleId(null);
  }

  function resetCapabilityForm() {
    setCapabilityForm({
      provider: "",
      country_code: "",
      currency: "",
      direction: "",
      channel: "",
      rail: "",
      network: "",
      enabled: true,
    });
    setEditingCapabilityId(null);
  }

  function openEditRule(rule) {
    // Editing happens in place within the row, so make sure the
    // separate "create" row is closed while we do that.
    setShowRuleForm(false);
    setEditingRuleId(rule.id);
    setRuleForm({
      corridor_code: rule.corridor_code || "",
      sender_country: rule.sender_country || "",
      recipient_country: rule.recipient_country || "",
      send_currency: rule.send_currency || "",
      receive_currency: rule.receive_currency || "",
      funding_method: rule.funding_method || "",
      onramp_provider: rule.onramp_provider || "",
      onramp_rail: rule.onramp_rail || "",
      settlement_asset: rule.settlement_asset || "USDC",
      settlement_chain: rule.settlement_chain || "",
      offramp_provider: rule.offramp_provider || "",
      offramp_channel: rule.offramp_channel || "",
      offramp_network: rule.offramp_network || "",
      min_amount: rule.min_amount || 0,
      max_amount: rule.max_amount || 0,
      priority: rule.priority || 100,
      enabled: rule.enabled ?? true,
      valid_from: rule.valid_from ? rule.valid_from.substring(0, 16) : "",
      valid_to: rule.valid_to ? rule.valid_to.substring(0, 16) : "",
    });
  }

  function openEditCapability(cap) {
    setEditingCapabilityId(cap.id);
    setCapabilityForm({
      provider: cap.provider || "",
      country_code: cap.country_code || "",
      currency: cap.currency || "",
      direction: cap.direction || "",
      channel: cap.channel || "",
      rail: cap.rail || "",
      network: cap.network || "",
      enabled: cap.enabled ?? true,
    });
    setShowCapabilityForm(true);
  }

  async function handleSaveRule(event) {
    event.preventDefault();
    setActionError(null);

    if (
      !ruleForm.corridor_code ||
      !ruleForm.sender_country ||
      !ruleForm.recipient_country ||
      !ruleForm.send_currency ||
      !ruleForm.receive_currency ||
      !ruleForm.onramp_provider ||
      !ruleForm.offramp_provider
    ) {
      setActionError("Please complete the required routing rule fields.");
      return;
    }

    try {
      const payload = {
        ...ruleForm,
        min_amount: Number(ruleForm.min_amount) || 0,
        max_amount: Number(ruleForm.max_amount) || 0,
        priority: Number(ruleForm.priority) || 100,
        valid_from: ruleForm.valid_from ? new Date(ruleForm.valid_from).toISOString() : null,
        valid_to: ruleForm.valid_to ? new Date(ruleForm.valid_to).toISOString() : null,
      };

      if (editingRuleId) {
        await updateRule({ ruleId: editingRuleId, payload });
      } else {
        await createRule(payload);
      }

      resetRuleForm();
      setShowRuleForm(false);
    } catch (err) {
      setActionError(readApiError(err, "Could not save the routing rule."));
    }
  }

  async function handleSaveCapability(event) {
    event.preventDefault();
    setActionError(null);

    if (
      !capabilityForm.provider ||
      !capabilityForm.country_code ||
      !capabilityForm.currency ||
      !capabilityForm.direction
    ) {
      setActionError("Please complete the required provider capability fields.");
      return;
    }

    try {
      const payload = {
        provider: capabilityForm.provider,
        country_code: capabilityForm.country_code,
        currency: capabilityForm.currency,
        direction: capabilityForm.direction,
        channel: capabilityForm.channel || null,
        rail: capabilityForm.rail || null,
        network: capabilityForm.network || null,
        enabled: capabilityForm.enabled,
      };

      if (editingCapabilityId) {
        await updateCapability({ capabilityId: editingCapabilityId, payload });
      } else {
        await createCapability(payload);
      }

      resetCapabilityForm();
      setShowCapabilityForm(false);
    } catch (err) {
      setActionError(readApiError(err, "Could not save the provider capability."));
    }
  }

  return (
    <div className="space-y-8 max-w-6xl text-left">
      {/* PAGE HEADER */}
      <div>
        <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
          Live Configuration
        </h2>
        <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
          Providers & Corridors
        </h1>
        <p className="text-xs text-slate-400 mt-2">
          Provider switches come from server environment configuration. Route and capability modifications update PostgreSQL records in real-time.
        </p>
      </div>

      {/* GLOBAL ERROR */}
      {(error || (actionError && !pendingAction)) && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          {actionError || "Could not load the current configuration."}
        </div>
      )}

      {/* RUNTIME READINESS */}
      <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
        <SectionTitle
          icon={ServerCog}
          title="Runtime readiness"
          subtitle="Safe, non-secret values from /health/configuration"
        />

        {isLoading ? (
          <Loading />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Readiness
              label="Environment"
              value={configuration?.environment || "unknown"}
              ready
            />
            <Readiness
              label="Encryption"
              value={configuration?.data_encryption_configured ? "Configured" : "Missing"}
              ready={configuration?.data_encryption_configured}
            />
            <Readiness
              label="Mock mode"
              value={configuration?.provider_mock_mode ? "Enabled" : "Disabled"}
              ready={!configuration?.provider_mock_mode}
            />
            <Readiness
              label="CORS origins"
              value={configuration?.cors_origin_count ?? 0}
              ready={(configuration?.cors_origin_count ?? 0) > 0}
            />
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(configuration?.providers || {}).map(([provider, enabled]) => (
            <div key={provider} className="rounded-2xl border border-slate-100 p-4">
              <p className="text-xs font-semibold text-[#0A2540] capitalize">
                {humanize(provider)}
              </p>
              <p className={`text-xs mt-1 font-medium ${enabled ? "text-emerald-600" : "text-slate-400"}`}>
                {enabled ? "Enabled" : "Disabled"}
              </p>
            </div>
          ))}
        </div>
      </section>

      <AdminLlmKeys />

      {/* ROUTING RULES */}
      <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <SectionTitle
            icon={Globe}
            title="Routing rules"
            subtitle="Priority-ordered corridors used during transfer confirmation"
          />
          <button
            type="button"
            onClick={() => {
              setActionError(null);
              resetRuleForm();
              setShowRuleForm((value) => !value);
            }}
            className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#0A4730] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#0A4730]/90 active:scale-[0.98] transition"
          >
            {showRuleForm ? <X size={15} /> : <Plus size={15} />}
            <span className="hidden sm:inline">
              {showRuleForm ? "Close" : "Add rule"}
            </span>
          </button>
        </div>

        {/* SEARCH BAR FOR RULES */}
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={ruleSearchQuery}
            onChange={(e) => setRuleSearchQuery(e.target.value)}
            placeholder="Search routing rules by corridor, currency, country, or provider..."
            className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 text-xs text-[#0A2540] outline-none transition focus:bg-white focus:border-[#0A4730]/40 focus:ring-2 focus:ring-[#0A4730]/5 placeholder:text-slate-400"
          />
        </div>

        {/* ROUTING RULES LIST — create and edit forms render in place, inline within the list */}
        {isLoading ? (
          <Loading />
        ) : (
          <div className="divide-y divide-slate-100">
            {showRuleForm && !editingRuleId && (
              <InlineRuleForm
                isNew
                form={ruleForm}
                onFieldChange={updateRuleField}
                onSubmit={handleSaveRule}
                onCancel={() => {
                  resetRuleForm();
                  setShowRuleForm(false);
                }}
                submitting={isCreatingRule}
              />
            )}

            {filteredRoutingRules.length ? (
              filteredRoutingRules.map((rule) =>
                editingRuleId === rule.id ? (
                  <InlineRuleForm
                    key={rule.id}
                    form={ruleForm}
                    onFieldChange={updateRuleField}
                    onSubmit={handleSaveRule}
                    onCancel={resetRuleForm}
                    submitting={isUpdating}
                  />
                ) : (
                  <div key={rule.id} className="flex items-center justify-between gap-4 py-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0A2540] capitalize">
                        {rule.send_currency} → {rule.receive_currency}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 capitalize">
                        {rule.sender_country} → {rule.recipient_country} · {rule.onramp_provider} / {rule.offramp_provider} · priority {rule.priority}
                        {rule.funding_method ? ` · ${humanize(rule.funding_method)}` : ""}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setInspectItem(rule);
                          setInspectType("rule");
                        }}
                        title="Inspect rule details"
                        className="p-2 rounded-xl text-slate-400 hover:text-[#0A4730] hover:bg-slate-50 transition"
                      >
                        <Eye size={17} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditRule(rule)}
                        title="Edit rule"
                        className="p-2 rounded-xl text-slate-400 hover:text-[#0A4730] hover:bg-slate-50 transition"
                      >
                        <Edit3 size={17} />
                      </button>
                      {!rule.enabled && (
                        <button
                          type="button"
                          onClick={() => handleDeleteRule(rule)}
                          disabled={isDeletingRule}
                          title="Delete unused rule"
                          className="p-2 rounded-xl text-red-300 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                        >
                          <Trash2 size={17} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleRule(rule)}
                        disabled={isUpdating}
                        aria-label={`${rule.enabled ? "Disable" : "Enable"} rule`}
                        className={`disabled:opacity-50 ${rule.enabled ? "text-emerald-600" : "text-slate-300"}`}
                      >
                        {rule.enabled ? <ToggleRight size={34} /> : <ToggleLeft size={34} />}
                      </button>
                    </div>
                  </div>
                )
              )
            ) : (
              !showRuleForm && (
                <Empty text={routingRules.length ? "No routing rules match your search query." : "No routing rules are seeded."} />
              )
            )}
          </div>
        )}
      </section>

      {/* PROVIDER CAPABILITIES */}
      <section className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <SectionTitle
            icon={Database}
            title="Provider capabilities"
            subtitle="The execution matrix checked before a route can be selected"
          />
          <button
            type="button"
            onClick={() => {
              setActionError(null);
              resetCapabilityForm();
              setShowCapabilityForm((value) => !value);
            }}
            className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#0A4730] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#0A4730]/90 active:scale-[0.98] transition"
          >
            {showCapabilityForm ? <X size={15} /> : <Plus size={15} />}
            <span className="hidden sm:inline">
              {showCapabilityForm ? "Close" : "Add capability"}
            </span>
          </button>
        </div>

        {/* SEARCH BAR FOR CAPABILITIES */}
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={capabilitySearchQuery}
            onChange={(e) => setCapabilitySearchQuery(e.target.value)}
            placeholder="Search capabilities by provider, country, currency, or channel..."
            className="w-full h-11 rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 text-xs text-[#0A2540] outline-none transition focus:bg-white focus:border-[#0A4730]/40 focus:ring-2 focus:ring-[#0A4730]/5 placeholder:text-slate-400"
          />
        </div>

        {/* CREATE / EDIT CAPABILITY FORM */}
        {showCapabilityForm && (
          <form
            onSubmit={handleSaveCapability}
            className="rounded-3xl bg-slate-50 border border-slate-100 p-5 md:p-6 space-y-6"
          >
            <div>
              <h4 className="text-sm font-semibold text-[#0A4730]">
                {editingCapabilityId ? "Edit provider capability" : "Create provider capability"}
              </h4>
              <p className="text-xs text-slate-400 mt-1">
                Define a provider's supported payout or onramp capability.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                label="Provider"
                required
                value={capabilityForm.provider}
                onChange={(value) => updateCapabilityField("provider", value)}
                placeholder="zuba"
              />
              <FormField
                label="Country code"
                required
                value={capabilityForm.country_code}
                onChange={(value) => updateCapabilityField("country_code", value.toUpperCase())}
                placeholder="KE"
              />
              <FormField
                label="Currency"
                required
                value={capabilityForm.currency}
                onChange={(value) => updateCapabilityField("currency", value.toUpperCase())}
                placeholder="KES"
              />
              <FormField
                label="Direction"
                required
                value={capabilityForm.direction}
                onChange={(value) => updateCapabilityField("direction", value)}
                placeholder="offramp"
              />
              <FormField
                label="Channel"
                value={capabilityForm.channel}
                onChange={(value) => updateCapabilityField("channel", value)}
                placeholder="mobile_money"
              />
              <FormField
                label="Rail"
                value={capabilityForm.rail}
                onChange={(value) => updateCapabilityField("rail", value)}
                placeholder="provider_managed"
              />
              <FormField
                label="Network"
                value={capabilityForm.network}
                onChange={(value) => updateCapabilityField("network", value)}
                placeholder="MPESA_KE"
              />
            </div>

            <ToggleField
              label="Enable capability immediately"
              checked={capabilityForm.enabled}
              onChange={(value) => updateCapabilityField("enabled", value)}
            />

            <FormActions
              onCancel={() => {
                resetCapabilityForm();
                setShowCapabilityForm(false);
              }}
              submitting={isCreatingCapability || isUpdating}
              label={editingCapabilityId ? "Save changes" : "Create capability"}
            />
          </form>
        )}

        {isLoading ? (
          <Loading />
        ) : filteredCapabilities.length ? (
          <div className="divide-y divide-slate-100">
            {filteredCapabilities.map((capability) => (
              <div key={capability.id} className="flex items-center justify-between gap-4 py-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0A2540] capitalize">
                    {capability.provider} · {capability.country_code} / {capability.currency}
                  </p>
                  <p className="text-xs text-slate-400 mt-1 capitalize">
                    {humanize(capability.direction)} · {humanize(capability.channel || capability.rail || "provider managed")}
                    {capability.network ? ` · ${capability.network}` : ""}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setInspectItem(capability);
                      setInspectType("capability");
                    }}
                    title="Inspect capability details"
                    className="p-2 rounded-xl text-slate-400 hover:text-[#0A4730] hover:bg-slate-50 transition"
                  >
                    <Eye size={17} />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditCapability(capability)}
                    title="Edit capability"
                    className="p-2 rounded-xl text-slate-400 hover:text-[#0A4730] hover:bg-slate-50 transition"
                  >
                    <Edit3 size={17} />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCapability(capability)}
                    disabled={isUpdating}
                    aria-label={`${capability.enabled ? "Disable" : "Enable"} capability`}
                    className={`disabled:opacity-50 ${capability.enabled ? "text-emerald-600" : "text-slate-300"}`}
                  >
                    {capability.enabled ? <ToggleRight size={34} /> : <ToggleLeft size={34} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty text={capabilities.length ? "No provider capabilities match your search query." : "No provider capabilities are seeded."} />
        )}
      </section>

      {/* INSPECTION MODAL */}
      {inspectItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 space-y-6 shadow-xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-serif font-semibold text-[#0A4730] capitalize">
                  {inspectType === "rule" ? "Routing rule details" : "Capability details"}
                </h3>
                <p className="text-xs text-slate-400 font-mono mt-0.5">
                  ID: {inspectItem.id}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInspectItem(null)}
                className="p-2 rounded-xl text-slate-400 hover:bg-slate-50 transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {Object.entries(inspectItem).map(([key, val]) => (
                <div key={key} className="flex flex-col sm:flex-row sm:justify-between py-1 border-b border-slate-50 gap-1">
                  <span className="font-semibold text-[#0A2540]/60 uppercase tracking-wider text-[10px]">
                    {humanize(key)}
                  </span>
                  <span className="font-mono text-[#0A2540] break-all sm:text-right">
                    {val === null || val === undefined ? (
                      <span className="text-slate-300 italic">null</span>
                    ) : typeof val === "boolean" ? (
                      val ? "True" : "False"
                    ) : (
                      String(val)
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setInspectItem(null)}
                className="h-10 px-5 rounded-xl bg-[#0A4730] text-white text-xs font-semibold hover:bg-[#0A4730]/90 transition"
              >
                Close inspection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WARNING */}
      <div className="flex items-start gap-3 rounded-2xl bg-amber-50 border border-amber-100 p-4 text-xs text-amber-800">
        <Shield size={17} className="shrink-0 mt-0.5" />
        <p>
          Disabling a rule or capability immediately removes it from route selection. Enabling a database record does not enable the provider itself; provider environment flags and credentials must also be configured on the backend.
        </p>
      </div>

      <ConfirmActionModal
        open={Boolean(pendingAction)}
        title={pendingAction?.title || ""}
        description={pendingAction?.description}
        confirmLabel={pendingAction?.confirmLabel}
        destructive={pendingAction?.destructive}
        requireReason={pendingAction?.requireReason}
        error={actionError}
        isSubmitting={isUpdating || isDeletingRule}
        onConfirm={runPendingAction}
        onCancel={() => {
          setPendingAction(null);
          setActionError(null);
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────── */

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-slate-100 min-w-0">
      <div className="w-10 h-10 rounded-2xl bg-[#C0F6B7]/40 text-[#0A4730] grid place-items-center shrink-0">
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-serif font-semibold text-[#0A4730]">
          {title}
        </h3>
        <p className="text-xs text-slate-400">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   INLINE ROUTING RULE FORM (renders in place
   within the list — used for both create and
   edit, replacing the row being edited)
───────────────────────────────────────────── */

function InlineRuleForm({ form, onFieldChange, onSubmit, onCancel, submitting, isNew = false }) {
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl bg-slate-50 border border-slate-100 p-5 md:p-6 my-2 space-y-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-[#0A4730]">
            {isNew ? "Create routing rule" : "Edit routing rule"}
          </h4>
          <p className="text-xs text-slate-400 mt-1">
            {isNew
              ? "Define the corridor and provider path used for matching transfers."
              : "Editing in place — changes apply to this rule only."}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="p-2 rounded-xl text-slate-400 hover:text-[#0A4730] hover:bg-white transition disabled:opacity-50"
          title="Cancel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <FormField
          label="Corridor code"
          required
          value={form.corridor_code}
          onChange={(value) => onFieldChange("corridor_code", value)}
          placeholder="GB-KE"
        />
        <FormField
          label="Sender country"
          required
          value={form.sender_country}
          onChange={(value) => onFieldChange("sender_country", value.toUpperCase())}
          placeholder="GB"
        />
        <FormField
          label="Recipient country"
          required
          value={form.recipient_country}
          onChange={(value) => onFieldChange("recipient_country", value.toUpperCase())}
          placeholder="KE"
        />
        <FormField
          label="Send currency"
          required
          value={form.send_currency}
          onChange={(value) => onFieldChange("send_currency", value.toUpperCase())}
          placeholder="GBP"
        />
        <FormField
          label="Receive currency"
          required
          value={form.receive_currency}
          onChange={(value) => onFieldChange("receive_currency", value.toUpperCase())}
          placeholder="KES"
        />
        <FormField
          label="Funding method"
          value={form.funding_method}
          onChange={(value) => onFieldChange("funding_method", value)}
          placeholder="bank"
        />
        <FormField
          label="Onramp provider"
          required
          value={form.onramp_provider}
          onChange={(value) => onFieldChange("onramp_provider", value)}
          placeholder="bridge"
        />
        <FormField
          label="Onramp rail"
          value={form.onramp_rail}
          onChange={(value) => onFieldChange("onramp_rail", value)}
          placeholder="gbp.fps"
        />
        <FormField
          label="Settlement asset"
          value={form.settlement_asset}
          onChange={(value) => onFieldChange("settlement_asset", value)}
          placeholder="USDC"
        />
        <FormField
          label="Settlement chain"
          value={form.settlement_chain}
          onChange={(value) => onFieldChange("settlement_chain", value)}
          placeholder="provider_managed"
        />
        <FormField
          label="Offramp provider"
          required
          value={form.offramp_provider}
          onChange={(value) => onFieldChange("offramp_provider", value)}
          placeholder="zuba"
        />
        <FormField
          label="Offramp channel"
          value={form.offramp_channel}
          onChange={(value) => onFieldChange("offramp_channel", value)}
          placeholder="mobile_money"
        />
        <FormField
          label="Offramp network"
          value={form.offramp_network}
          onChange={(value) => onFieldChange("offramp_network", value)}
          placeholder="MPESA_KE"
        />
        <FormField
          label="Minimum amount"
          type="number"
          value={form.min_amount}
          onChange={(value) => onFieldChange("min_amount", value)}
          placeholder="0"
        />
        <FormField
          label="Maximum amount"
          type="number"
          value={form.max_amount}
          onChange={(value) => onFieldChange("max_amount", value)}
          placeholder="0"
        />
        <FormField
          label="Priority"
          type="number"
          value={form.priority}
          onChange={(value) => onFieldChange("priority", value)}
          placeholder="100"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          label="Valid from"
          type="datetime-local"
          value={form.valid_from}
          onChange={(value) => onFieldChange("valid_from", value)}
        />
        <FormField
          label="Valid to"
          type="datetime-local"
          value={form.valid_to}
          onChange={(value) => onFieldChange("valid_to", value)}
        />
      </div>

      <ToggleField
        label={isNew ? "Enable rule immediately" : "Rule enabled"}
        checked={form.enabled}
        onChange={(value) => onFieldChange("enabled", value)}
      />

      <FormActions
        onCancel={onCancel}
        submitting={submitting}
        label={isNew ? "Create routing rule" : "Save changes"}
      />
    </form>
  );
}

/* ─────────────────────────────────────────────
   FORM FIELD
───────────────────────────────────────────── */

function FormField({ label, required = false, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[9px] uppercase tracking-[0.16em] font-semibold text-[#0A2540]/50 mb-2">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-[#0A2540] outline-none transition focus:border-[#0A4730]/40 focus:ring-2 focus:ring-[#0A4730]/5 placeholder:text-slate-300"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOGGLE FIELD
───────────────────────────────────────────── */

function ToggleField({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-3 text-left"
    >
      {checked ? (
        <ToggleRight size={30} className="text-emerald-600" />
      ) : (
        <ToggleLeft size={30} className="text-slate-300" />
      )}
      <div>
        <p className="text-xs font-semibold text-[#0A2540]">
          {label}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {checked ? "Enabled" : "Disabled"}
        </p>
      </div>
    </button>
  );
}

/* ─────────────────────────────────────────────
   FORM ACTIONS
───────────────────────────────────────────── */

function FormActions({ onCancel, submitting, label }) {
  return (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        className="h-11 px-5 rounded-xl text-xs font-semibold text-[#0A2540]/60 hover:bg-white disabled:opacity-50 transition"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={submitting}
        className="h-11 px-5 rounded-xl bg-[#0A4730] text-white text-xs font-semibold hover:bg-[#0A4730]/90 active:scale-[0.98] disabled:opacity-50 transition"
      >
        {submitting ? "Saving…" : label}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   EXISTING UI HELPERS
───────────────────────────────────────────── */

function Readiness({ label, value, ready }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <p className="text-[9px] uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <p className={`mt-2 text-sm font-semibold ${ready ? "text-[#0A4730]" : "text-red-500"}`}>
        {value}
      </p>
    </div>
  );
}

function Loading() {
  return (
    <p className="text-sm text-slate-400 py-6">
      Loading configuration…
    </p>
  );
}

function Empty({ text }) {
  return (
    <p className="text-sm text-slate-400 py-6">
      {text}
    </p>
  );
}

function humanize(value = "") {
  return String(value).replaceAll("_", " ");
}